// src/components/issues/FenetreQualite.tsx — SCE Issues
// La fenêtre des déclarations qualité — NC-S (05/10) et FNC-T (lot F2, 06/10) —
// et les styles de leurs champs : une seule définition, pour que les deux
// formulaires se ressemblent et se comportent pareil (volet de la fiche au
// bureau large, centrée au bureau, par le bas au téléphone ; le fond ne ferme
// rien).

import type { CSSProperties, JSX, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Minus } from 'lucide-react'

/** À partir de quelle largeur d'écran la fenêtre s'ouvre en volet de la fiche. */
export const ECRAN_VOLET = '(min-width: 1180px)'

export type Disposition = 'volet' | 'centre' | 'bas'

export const STYLE_ETIQUETTE: CSSProperties = {
  fontSize: 'var(--font-size-info)',
  color: 'var(--color-text-secondary)',
  display: 'block',
  marginBottom: 6,
}

export const STYLE_CHAMP: CSSProperties = {
  width: '100%',
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-copy2)',
  fontFamily: 'inherit',
}

export const STYLE_AIDE: CSSProperties = {
  fontSize: 'var(--font-size-info)',
  color: 'var(--color-text-secondary)',
  margin: 0,
}

export const STYLE_SECTION: CSSProperties = {
  fontSize: 'var(--font-size-copy2)',
  fontWeight: 600,
  margin: 0,
}

/**
 * La fenêtre : volet de la fiche, centrée, ou par le bas (`disposition`).
 * En volet, rendue dans la colonne de droite de la fenêtre de la fiche ;
 * sinon à la racine de la page. Son fond ne ferme RIEN : seul « Réduire »
 * (ou Échap) la range.
 */
export function Fenetre({
  disposition,
  volet,
  titre,
  sousTitre,
  onReduire,
  libelleReduire,
  desactive,
  pied,
  children,
}: {
  disposition: Disposition
  volet: HTMLElement | null
  titre: string
  sousTitre?: string
  onReduire: () => void
  libelleReduire: string
  desactive?: boolean
  pied?: ReactNode
  children: ReactNode
}): JSX.Element {
  return createPortal(
    <>
      {disposition !== 'volet' && <div className="ncs-voile" role="presentation" />}
      <div
        className="ncs-fenetre"
        data-disposition={disposition}
        role="dialog"
        aria-modal={disposition !== 'volet'}
        aria-label={titre}
      >
        <div className="ncs-entete">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 className="ncs-titre">{titre}</h3>
            {sousTitre && <p className="ncs-sous-titre">{sousTitre}</p>}
          </div>
          <button
            type="button"
            className="ncs-reduire"
            onClick={onReduire}
            disabled={desactive}
            title={libelleReduire === 'Réduire' ? 'Réduire : la saisie est gardée, à reprendre depuis la fiche' : undefined}
          >
            {libelleReduire === 'Réduire' && <Minus size={18} />}
            {libelleReduire}
          </button>
        </div>
        <div className="ncs-corps">{children}</div>
        {pied && <div className="ncs-pied">{pied}</div>}
      </div>
    </>,
    disposition === 'volet' && volet ? volet : document.body,
  )
}
